// @ts-nocheck
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { cartProducts, calculateTotalPrice, clearCart, calculateTotalPriceEuro } from "../stores/cart/cartSlice";
import { ProductsSummary } from "./ProductsSummary";
import { addOrder } from "../stores/userInfo/detailsSlice";
import { selectProductsUsedFallback } from "../stores/menu/productsSlice";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useLanguageContext } from "../context/languageContext";
import { FaCreditCard, FaMoneyBillWave, FaMapMarkerAlt, FaWifi, FaInfoCircle, FaCheckDouble } from "react-icons/fa";
import { API_URL } from "../config/api";

// Helper: calculate distance
const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c * 1000;
};

const checkLocation = (businessLat, businessLng, maxDistance = 800) =>
    new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(false);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const dist = getDistance(pos.coords.latitude, pos.coords.longitude, businessLat, businessLng);
                resolve(dist <= maxDistance);
            },
            () => resolve(false),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    });

const checkIP = async (allowedIP) => {
    try {
        const res = await fetch("https://api.ipify.org?format=json");
        const data = await res.json();
        return data.ip === allowedIP;
    } catch { return false; }
};

const SummaryDetails = () => {
    const cart = useSelector(cartProducts);
    const totalPrice = useSelector(calculateTotalPrice);
    const totalEuroPrice = useSelector(calculateTotalPriceEuro);
    const business = useSelector((state) => state.business);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useLanguageContext();
    const { register, handleSubmit, formState: { errors }, watch } = useForm({
        defaultValues: { paymentMethod: "CASH" }
    });
    const [submitting, setSubmitting] = useState(false);
    const paymentMethod = watch("paymentMethod");
    // The API is unreachable, so the POST below cannot land. Better to say so than
    // to let the guest fill the form and watch the button do nothing.
    const offline = useSelector(selectProductsUsedFallback);

    const PUBLIC_TOKEN = "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkZW5pcy5taW1pbmlAY2xpY2thbGJhbmlhLmNvbSIsImlhdCI6MTc1MDk4MjU3NywiZXhwIjoxNzUxMjQxNzc3fQ.yQ3xGQEfpj62ruJoLJYOl3966g55OlyofRqjoAHzu";

    const onSubmit = async (data) => {
        setSubmitting(true);
        try {
            const businessLat = 41.313436;
            const businessLng = 19.475683;
            const businessIP = "46.252.34.88";

            const locationOk = await checkLocation(businessLat, businessLng);
            const ipOk = await checkIP(businessIP);

            if (!locationOk && !ipOk) {
                alert(t("must_be_near"));
                return setSubmitting(false);
            }

            const formData = {
                menuItems: cart,
                business: business.businessInfo,
                tableNo: data.tableNo,
                description: data.extraDetails,
                total: totalPrice,
                orderPayment: data.paymentMethod,
                timestamp: Date.now(),
                status: "PENDING",
            };

            const response = await fetch(`${API_URL}/api/order`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${PUBLIC_TOKEN}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                const savedOrder = await response.json();
                dispatch(addOrder(savedOrder));
                navigate("/order-status");
                dispatch(clearCart());
            }
        } catch (error) {
            console.error("Order error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="animate-app-reveal flex flex-col min-h-[calc(100vh-8rem)]" onSubmit={handleSubmit(onSubmit)}>
            <div className="px-5 py-6 space-y-8 flex-1">
                {/* Order Summary Block */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-black uppercase tracking-[0.15em] text-slate-400">{t("orderedItems")}</h2>
                        <button type="button" onClick={() => navigate('/menu')} className="text-xs font-black uppercase text-indigo-600 tracking-wider">{t("Add More")}</button>
                    </div>
                    <ProductsSummary />
                </div>

                {/* Logistics Section */}
                <div className="space-y-6 pt-4 border-t border-slate-50">
                    <h2 className="text-sm font-black uppercase tracking-[0.15em] text-slate-400">{t("orderDetails")}</h2>

                    <div className="grid gap-5">
                        {/* Table Number - Sharp Styled */}
                        <div className="space-y-1.5 focus-within:text-indigo-600 transition-colors">
                            <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">{t("tableNo")}</label>
                            <input
                                {...register('tableNo', { required: true })}
                                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3.5 text-slate-900 font-bold placeholder:text-slate-300 placeholder:font-medium focus:bg-white focus:border-indigo-600/20 focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all"
                                placeholder={t("enterTableNo")}
                            />
                            {errors.tableNo && <span className="text-xs font-bold text-red-500 uppercase tracking-tighter ml-1">Required</span>}
                        </div>

                        {/* Special Instructions */}
                        <div className="space-y-1.5 focus-within:text-indigo-600 transition-colors">
                            <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">{t("description")}</label>
                            <textarea
                                {...register('extraDetails')}
                                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3.5 text-slate-900 font-medium placeholder:text-slate-300 focus:bg-white focus:border-indigo-600/20 focus:ring-4 focus:ring-indigo-600/5 outline-none transition-all min-h-[80px] resize-none"
                                placeholder={t("extraDetails")}
                            />
                        </div>

                        {/* Payment Selector - App Style */}
                        <div className="space-y-3">
                            <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">{t("paymentMethod")}</label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'CASH' ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-100'}`}>
                                    <input type="radio" {...register('paymentMethod')} value="CASH" className="hidden" />
                                    <FaMoneyBillWave size={18} />
                                    <span className="text-xs font-bold uppercase tracking-widest">{t("cash")}</span>
                                </label>
                                <label className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${paymentMethod === 'CREDIT_CARD' ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-100'}`}>
                                    <input type="radio" {...register('paymentMethod')} value="CREDIT_CARD" className="hidden" />
                                    <FaCreditCard size={18} />
                                    <span className="text-xs font-bold uppercase tracking-widest">{t("creditCard")}</span>
                                </label>
                            </div>
                        </div>

                        {paymentMethod === "CREDIT_CARD" && (
                            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-100/50 flex items-start gap-3">
                                <FaInfoCircle className="text-amber-500 mt-0.5" size={14} />
                                <p className="text-xs text-amber-700/80 font-medium leading-relaxed">{t("creditCardNote")}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Verification Pills */}
                <div className="flex flex-wrap gap-2 pt-4">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-black uppercase tracking-wider text-slate-400">
                        <FaMapMarkerAlt /> 120m {t("Nearby")}
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-black uppercase tracking-wider text-slate-400">
                        <FaWifi /> {t("Secure Network")}
                    </div>
                </div>
            </div>

            {/* Fixed Bottom Action Bar */}
            <div className="sticky bottom-0 p-5 bg-white border-t border-slate-50 shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.05)] space-y-4">
                <div className="flex items-end justify-between px-1">
                    <div className="space-y-0.5">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t("Est. Total")}</p>
                        <p className="text-2xl font-clash text-slate-950 tracking-tighter leading-none">{totalPrice} ALL</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs font-bold text-slate-400">{totalEuroPrice} €</p>
                    </div>
                </div>

                {offline && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100/60">
                        <FaWifi className="text-amber-500 mt-0.5 flex-shrink-0" size={12} />
                        <p className="text-[11px] font-medium leading-snug text-amber-700/90">
                            {t("offlineMenuNotice", "Showing the saved menu - ordering is unavailable right now.")}
                        </p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting || offline}
                    className="btn-app btn-primary-app w-full flex items-center justify-between group h-14 disabled:opacity-40 disabled:pointer-events-none"
                >
                    <span className="text-sm tracking-[0.15em] uppercase pl-2">
                        {submitting ? t('Processing...') : t("Confirm Order")}
                    </span>
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-active:scale-90 transition-transform">
                        {submitting ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : (
                            <FaCheckDouble size={14} />
                        )}
                    </div>
                </button>
            </div>
        </form>
    );
};

export default SummaryDetails;
